import { addMilliseconds } from 'date-fns'
import { cloneDeep, isEqual } from 'lodash'
import type { Ref } from 'vue'
import { ref, watch, watchEffect } from 'vue'
import type { Keys } from '../types'

const getKeys = Object.keys as <T>(obj: T) => Keys<T>

export function useUpdatableState<T>(
  externalStateRef: Ref<T>,
  onUpdate: (value: T) => void,
  clone: (value: T) => T = cloneDeep,
): Ref<T> {
  const localRef = ref()
  watchEffect(() => {
    localRef.value = clone(externalStateRef.value)
  })
  watch(
    localRef,
    (newValue) => {
      if (!isEqual(newValue, externalStateRef.value)) {
        onUpdate(newValue)
      }
    },
    { deep: true },
  )
  return localRef
}

export function useAsyncFunction<F extends (...args: any[]) => ReturnType<F>>(
  fn: F,
): { isLoading: Ref<boolean>; fn: (...args: Parameters<F>) => ReturnType<F> } {
  const isLoading = ref(false)
  function wrapper(...args: Parameters<F>): ReturnType<F> {
    isLoading.value = true
    try {
      return fn(...args)
    } finally {
      isLoading.value = false
    }
  }
  return { fn: wrapper, isLoading }
}

export function getCookie(key: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const cookie = (document?.cookie ?? '')
    .split(';')
    .map((cookieStr) => cookieStr.trim())
    .map((cookieStr) => {
      const [key, value] = cookieStr.split('=', 2)
      return { key, value: decodeURIComponent(value) }
    })
    .find((cookie) => cookie.key === key)
  return cookie?.value
}

export function objectToFormData<T = Record<string, string | number | boolean | File>>(
  obj: T,
  keys: Keys<T>,
): FormData {
  keys = keys ?? getKeys(obj)
  const formData = new FormData()
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'undefined') continue

    if (typeof value === 'boolean') {
      if (value) {
        formData.set(key, 'on')
      }
    } else if (typeof value === 'number') {
      formData.set(key, value.toString())
    } else if (typeof value === 'string' || value instanceof File) {
      formData.set(key, value)
    } else {
      throw new TypeError(`Unhandled type: ${typeof value} for value ${value}`)
    }
  }
  return formData
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function mapToDomain(
  value: number,
  inputDomain: [number, number],
  outputDomain: [number, number],
) {
  const [x1, y1] = inputDomain
  const [x2, y2] = outputDomain
  value = clamp(value, x1, y1)
  return ((value - x1) * (y2 - x2)) / (y1 - x1) + x2
}

export function differenceInSeconds(dateLeft: Date, dateRight: Date) {
  return (dateLeft.getTime() - dateRight.getTime()) / 1000
}

// addSeconds from date-fns does not support fractional seconds
export function addSeconds(date: Date, amount: number) {
  return addMilliseconds(date, amount * 1000)
}

type URLToken = string | number
type URLBuilder = (...subPaths: URLToken[] | [...URLToken[], URLToken | URLSearchParams]) => string
type PrefixableURLBuilder = URLBuilder & {
  prefix: (...prefixes: URLToken[]) => PrefixableURLBuilder
}

export function createURLBuilder(basepath: string, useTrailingSlash = true): PrefixableURLBuilder {
  // Strip all trailing slashes from the basepath.
  // We handle slashes when building URLs.
  basepath = basepath.replace(/\/*$/, '')

  const buildURL: PrefixableURLBuilder = (...subPaths) => {
    let params
    if (subPaths.at(-1) instanceof URLSearchParams) {
      params = subPaths.pop()
    }
    if (subPaths.some((path) => String(path).includes('/'))) {
      throw new Error('Subpaths must not contain slashes')
    }
    const subPath = subPaths.length > 0 ? '/' + subPaths.join('/') : ''
    const url = basepath + subPath + (useTrailingSlash ? '/' : '')
    return params ? url + `?${params}` : url
  }

  buildURL.prefix = function (...prefixes: URLToken[]) {
    return createURLBuilder(buildURL(...prefixes), useTrailingSlash)
  }

  return buildURL
}
